import type { PracticeScenario } from '@/domain/sarah'

// Deterministic P3 practice scenarios, one per seeded episode topic. This is
// the "short practice scenario" allowed in P3 — NOT the branching Interactive
// Story / Choose Your Own Adventure engine, which is explicitly P4 scope.
// Each scenario is a single question with a single correct option.
export const sarahScenarios: PracticeScenario[] = [
  {
    id: 'scenario-phishing-urgent-link',
    topic: 'phishing',
    relatedEpisodeId: 'ep-phishing-trap',
    setup:
      'You receive a text message: "URGENT: Your account will be suspended today. Verify immediately using the link below." What would you do?',
    options: [
      { id: 'a', text: 'Click the link right away, before the deadline' },
      { id: 'b', text: 'Reply to the message asking for more details' },
      { id: 'c', text: "Ignore the link and check your account through the organization's official app or website" },
      { id: 'd', text: 'Forward it to a few colleagues to see what they think' },
    ],
    correctOptionId: 'c',
    correctFeedback:
      "Correct. Urgency is the tell here — real account issues don't need to be resolved through a link in a text message. Going straight to the official app or site (never a link someone sent you) is the safe move, just like Amaka should have in \"The Phishing Trap.\"",
    incorrectFeedback:
      "Not quite. This message is built to make you panic and click fast — that urgency is the trap. The safer move is to ignore the link entirely and check your account directly through the organization's official app or website.",
  },
  {
    id: 'scenario-simswap-signal-loss',
    topic: 'sim-swap',
    relatedEpisodeId: 'ep-simswap-shadows',
    setup:
      'Your phone suddenly shows "No Service" for no reason, and a few minutes later you get an SMS saying your mobile money PIN was changed — but you didn\'t change it. What should you do first?',
    options: [
      { id: 'a', text: 'Wait a few hours to see if signal comes back on its own' },
      { id: 'b', text: 'Contact your mobile carrier and bank immediately to report a possible SIM swap' },
      { id: 'c', text: 'Restart your phone repeatedly' },
      { id: 'd', text: 'Post about it on social media to warn friends' },
    ],
    correctOptionId: 'b',
    correctFeedback:
      "Correct. Sudden signal loss plus an account change you didn't make is the classic SIM-swap pattern — exactly what happened to Kwame. Contacting your carrier and bank right away can lock the attacker out before more damage is done.",
    incorrectFeedback:
      "That loses time you don't have. Sudden signal loss plus an unrequested account change is a strong SIM-swap signal — contact your mobile carrier and bank immediately so they can lock down the account.",
  },
  {
    id: 'scenario-password-reuse',
    topic: 'passwords',
    relatedEpisodeId: 'ep-password-fortress',
    setup:
      'You just heard that a website you use had a data breach. You use the same password on that site and your email account. What should you do?',
    options: [
      { id: 'a', text: "Do nothing — you weren't using that site much anyway" },
      { id: 'b', text: 'Change the password on the breached site only' },
      { id: 'c', text: 'Change the password everywhere you reused it, starting with your email, and turn on multi-factor authentication where you can' },
      { id: 'd', text: 'Wait for the company to email you before doing anything' },
    ],
    correctOptionId: 'c',
    correctFeedback:
      "Correct. A breached password is compromised everywhere it was reused — email first, since it's usually the key to resetting everything else. Turning on multi-factor authentication adds a second lock, just like the habits Tunde had to learn the hard way.",
    incorrectFeedback:
      "Reused passwords are the real risk here — if attackers got one password, they'll try it everywhere. Change it on every site you reused it, starting with email, and enable multi-factor authentication where available.",
  },
]

export function getScenarioByTopic(topic: string): PracticeScenario | null {
  return sarahScenarios.find((s) => s.topic === topic) ?? null
}

export function getScenarioForEpisode(episodeId: string): PracticeScenario | null {
  return sarahScenarios.find((s) => s.relatedEpisodeId === episodeId) ?? null
}

export function getScenarioById(id: string): PracticeScenario | null {
  return sarahScenarios.find((s) => s.id === id) ?? null
}
